import { FC, useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import CloseIcon from '../../assets/icons/CloseIcon';
import ChevronDownIcon from '../../assets/icons/ChevronDownIcon';
import { capitalize } from 'lodash';
import { Group } from '@mui/icons-material';
import ResetIcon from '../../assets/icons/ResetIcon';
import DateFilter from './DateFilter';
import { useTheme } from '../../middleware/ThemeContext';
import { CorporateTheme } from '../../utils/themes';
import DateRangeFilter from './DateRangeFilter';
import SearchIcon from '../../assets/icons/SearchIcon';
import { date } from 'yup';
import CalendarIcon from '../../assets/icons/CalendarIcon';
import CustomInput from '../form/CustomInput';
import { FormContainer, ModalContent, ModalOverlay } from '../FormBuilder/CustomForm/FormFiller';
import FormTitle from '../form/FormTitle';
import StyledText from '../StyledComponents/StyledText';
import { FieldLabel } from '../FormBuilder/FieldSettings/FieldSettingsModal';
import SearchBar from '../searchBar/SearchBar';
import SimpleBar from 'simplebar-react';
import { formatDateDefault } from '../../utils/helper';
import NoData from './NoData';

interface FiltersProps {
  filters: Record<string, string[]>;
  onFilterChange: (filters: Record<string, string[] | string>) => void;
  onCreateDate?: (date: Date | null) => void;
  isFiltersOpen: boolean;
  setShowFilters: (show: boolean) => void;
  filterGroups: {
    dataType?: string;
    key: string;
    label: string;
    options: { value: string | number; label: string }[] | string[];
  }[];
  startDate?: Date | null;
  endDate?: Date | null;
  routeKey?: string;
  query?: boolean;
  setFiltersContainerHeight?: (height: number) => void;
}

const FilterContainer = styled.div`
  position: relative;
  // align-items: center;
`;

const FilterPanel = styled.div`
  padding: ${CorporateTheme.spacing.md};
`;

const FiltersRow = styled.div`
  // display: flex;
  // gap: ${CorporateTheme.spacing.xs};
  // flex-wrap: wrap;
  // row-gap: ${CorporateTheme.spacing.sm};
`;

const FilterGroup = styled.div`
  position: relative;
`;

const DropdownTrigger = styled.button`
  display: flex;
  justify-content: space-between; /* 👈 keeps label left, chevron right */
  align-items: center;
  gap: ${CorporateTheme?.spacing.sm};
  padding: ${CorporateTheme?.spacing.sm};
  border-radius: ${CorporateTheme.borderRadius};
  background: ${({ theme }) => theme.colors.contentBackground};
  border: 1.5px solid ${({ theme }) => theme.CorporateTheme.colors.border};
  cursor: pointer;
  color: ${({ theme }) => theme.colors.placeholder};
  font-family: 'Raleway', sans-serif;
  font-size: 14px;
  font-weight: 500;
  width: 100%;   /* 👈 takes full width */
  min-width: unset; /* 👈 removes restriction */
  
  &:focus {
    outline: none;
  }
  &:hover {
    background: ${({ theme }) => theme.colors.topBarsColorHover};
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%; /* match trigger */
  background: ${({ theme }) => theme.CreateFormTheme.colors.inputBackground};
  border: 1px solid ${({ theme }) => theme.CreateFormTheme.colors.inputBorders};
  border-radius: ${CorporateTheme.borderRadius};
  margin-top: ${CorporateTheme.spacing.xs};
  z-index: 101;
  padding: ${CorporateTheme.spacing.sm};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);

  /* ❌ REMOVE max-height & overflow-y from here */
`;


const CheckboxOption = styled.label`
  display: flex;
  align-items: center;
  gap: ${CorporateTheme.spacing.sm};
  padding: ${CorporateTheme.spacing.sm} ${CorporateTheme.spacing.md};
  cursor: pointer;
  font-size: 14px;
  color: ${({ theme }) => theme.CorporateTheme.colors.textSecondary};
  font-family: 'Raleway', sans-serif;
  font-weight: 500;
  &:hover {
    background: ${({ theme }) => theme.CorporateTheme.colors.labelBG};
  }
`;

const CheckboxInput = styled.input`
  margin: 0;
  accent-color: ${CorporateTheme.colors.accent};
`;

const AppliedFilters = styled.div`
  background: ${({ theme }) => theme.CorporateTheme.colors.filterBG};
  display: flex;
  align-items: center;
  gap: ${CorporateTheme.spacing.xs};
  padding-top: ${CorporateTheme.spacing.sm};
  padding-left:${CorporateTheme.spacing.md};
  flex-wrap: wrap;
`;
const FilterChip = styled.div`
  gap: ${CorporateTheme.spacing.xs};
  padding: ${CorporateTheme.spacing.xs} ${CorporateTheme.spacing.md};
  background: ${({ theme }) => theme.colors.contentBackground};
  border: 1px solid ${({ theme }) => theme.CorporateTheme.colors.border};
  color: ${({ theme }) => theme.CorporateTheme.colors.textSecondary};
  display: flex;
  margin-bottom:${CorporateTheme.spacing.sm};
  align-items: center;
  flex-direction:row; 
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
  font-family: 'Raleway', sans-serif;
  cursor: pointer;
`;

export const ResetButton = styled.div`
  padding: ${CorporateTheme.spacing.xs};
  background: ${({ theme }) => theme.colors.contentBackground};
  border: 1px solid ${({ theme }) => theme.CorporateTheme.colors.border};
  color: ${({ theme }) => theme.CorporateTheme.colors.textSecondary};
  display: flex;
  align-items: center;
  border-radius: 20px;
  cursor: pointer;
`;

const RemoveButton = styled.button`
  border: none;
  background: none;
  cursor: pointer;
  padding: 0;
  color: ${({ theme }) => theme.CorporateTheme.colors.textSecondary};
  display: flex;
  align-items: center;
  &:hover {
    color: ${({ theme }) => theme.CorporateTheme.colors.danger};
  }
`;
const CloseButton = styled.button`
  border: none;
  background: none;
  cursor: pointer;
  padding: 0;
  color: ${({ theme }) => theme.CorporateTheme.colors.textSecondary};

  &:hover {
    color: ${({ theme }) => theme.CorporateTheme.colors.danger};
  }
`;

const GroupOption = styled.span`
  color: ${({ theme }) => theme.CorporateTheme.colors.headerColor};
  font-size: 14px;
  font-family: 'Raleway', sans-serif;
  font-weight: 600;
`;
const GroupOptionLabel = styled.span`
  color: ${({ theme }) => theme.CorporateTheme.colors.textSecondary};
  font-size: 14px;
  font-family: 'Raleway', sans-serif;
  font-weight: 500;
`;
const FilterHeader = styled.div`
  justify-content: space-between;
  align-items: center;
`;

function cleanLabel(label: string): string {
  return label
    .replace(/^\w+[:.]/, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, char => char.toUpperCase());
}

// Helper function to safely parse and format date strings
function safeFormatDate(dateString: string): string {
  if (!dateString) return '';
  
  try {
    let dateObj: Date | null = null;
    
    // Try parsing as ISO string first
    dateObj = new Date(dateString);
    
    // If invalid, try alternative parsing strategies
    if (isNaN(dateObj.getTime())) {
      // Try parsing as moment-compatible string
      dateObj = new Date(dateString);
    }
    
    // If still invalid, try extracting just the date part (before T)
    if (isNaN(dateObj.getTime())) {
      const datePart = dateString.split('T')[0];
      dateObj = new Date(datePart);
    }
    
    // If valid, format using formatDateDefault
    if (!isNaN(dateObj.getTime())) {
      return formatDateDefault(dateObj);
    }
    
    // If all parsing fails, return raw string
    return dateString;
  } catch {
    return dateString;
  }
}

const Filters: FC<FiltersProps> = ({ filters, onFilterChange, setShowFilters, filterGroups, isFiltersOpen, routeKey, setFiltersContainerHeight }) => {
  const { theme } = useTheme();
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [searchTerms, setSearchTerms] = useState<Record<string, string>>({});
  const dropdownRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const modalRef = useRef<HTMLDivElement | null>(null);
  const [filterSearch, setFilterSearch] = useState("");
  const appliedFiltersRef = useRef<HTMLDivElement | null>(null);
  const activeDropdownContainerRef = useRef<HTMLDivElement | null>(null);
  const [numberRangeValues, setNumberRangeValues] = useState<
    Record<string, { min: string; max: string }>
  >({});


  useEffect(() => {
    setNumberRangeValues((prev) => {
      const updated = { ...prev };

      Object.keys(prev).forEach((key) => {
        if (!filters[key]) {
          delete updated[key];
        }
      });

      return updated;
    });
  }, [filters]);

  useEffect(() => {
    if (activeDropdown) {
      const container = dropdownRefs.current[activeDropdown] as HTMLDivElement | null;
      activeDropdownContainerRef.current = container;
      // Auto-close if active dropdown group was filtered out
      if (!container) {
        setActiveDropdown(null);
      }
    } else {
      activeDropdownContainerRef.current = null;
    }
  }, [activeDropdown]);

  useEffect(() => {
    const handleDropdownClose = (event: MouseEvent) => {
      // Close dropdown if click is outside the active dropdown
      if (
        activeDropdownContainerRef.current && 
        !activeDropdownContainerRef.current.contains(event.target as Node)
      ) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('click', handleDropdownClose);
    return () => document.removeEventListener('click', handleDropdownClose);
  }, [activeDropdown]);

  useEffect(() => {
    if (!isFiltersOpen) return;

    const handleModalClose = (event: MouseEvent) => {
      if (!modalRef.current?.contains(event.target as Node)) {
        setShowFilters(false);
      }
    };

    document.addEventListener('mousedown', handleModalClose);

    return () => {
      document.removeEventListener('mousedown', handleModalClose);
    };
  }, [isFiltersOpen]);

  const handleFilterToggle = (groupKey: string) => {
    setActiveDropdown(prev => (prev === groupKey ? null : groupKey));
  };

  const handleFilterChange = (groupKey: string, value: string, dataType?: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    if (dataType === 'select') {
      onFilterChange({
        ...filters,
        [groupKey]: value,
      });
    } else {
      const currentValues = Array.isArray(filters[groupKey]) ? filters[groupKey] : [];
      const newValues = e.target.checked
        ? [...currentValues, value]
        : currentValues.filter(v => v !== value);

      onFilterChange({
        ...filters,
        [groupKey]: newValues,
      });
    }
  };
  const handleSingleDateChange = (groupKey: string, date: Date | null) => {
    const formatLocalDateTime = (date: Date | null) => {
      if (!date) return null;

      const pad = (num: number) => num.toString().padStart(2, '0');

      const yyyy = date.getFullYear();
      const mm = pad(date.getMonth() + 1);
      const dd = pad(date.getDate());
      const hh = pad(date.getHours());
      const min = pad(date.getMinutes());
      const ss = pad(date.getSeconds());

      return `${yyyy}-${mm}-${dd}T${hh}:${min}:${ss}`;
    };

    onFilterChange({
      ...filters,
      [groupKey]: date ? formatLocalDateTime(date) ?? '' : '',
    });
  };

  const handleDateRangeChange = (groupKey: string, dates: [Date | null, Date | null]) => {
    // Validate both dates exist and are valid
    if (dates.length !== 2 || !dates[0] || !dates[1] || isNaN(dates[0].getTime()) || isNaN(dates[1].getTime())) {
      return;
    }

    const startOfDay = (date: Date) => {
      const d = new Date(date);
      d.setHours(0, 0, 0, 0);
      return d;
    };

    const endOfDay = (date: Date) => {
      const d = new Date(date);
      d.setHours(23, 59, 59, 999); // last millisecond of the day
      return d;
    };

    const formatLocalDateTime = (date: Date | null) => {
      if (!date || isNaN(date.getTime())) return null;

      const pad = (num: number) => num.toString().padStart(2, '0');

      const yyyy = date.getFullYear();
      const mm = pad(date.getMonth() + 1);
      const dd = pad(date.getDate());
      const hh = pad(date.getHours());
      const min = pad(date.getMinutes());
      const ss = pad(date.getSeconds());

      return `${yyyy}-${mm}-${dd}T${hh}:${min}:${ss}`;
    };

    const adjustedDates: [Date, Date] = [startOfDay(dates[0]), endOfDay(dates[1])];

    onFilterChange({
      ...filters,
      [groupKey]: adjustedDates
        .map(date => formatLocalDateTime(date))
        .filter((d): d is string => d !== null),
    });
  };
  const handleSearchChange = (groupKey: string) => (value: string) => {
    setSearchTerms(prev => ({
      ...prev,
      [groupKey]: value.toLowerCase()
    }));
  };
  const removeFilter = (groupKey: string, value: string) => {
    const current = filters[groupKey];
    let updated;

    if (Array.isArray(current)) {
      updated = current.filter(v => v !== value);
    } else {
      updated = '';
    }

    onFilterChange({
      ...filters,
      [groupKey]: updated,
    });
  };
  type FilterOption = {
    label: string;
    value: string | number;
  };
  const getOptionLabel = (groupKey: string, value: string): string => {
    const group = filterGroups.find(g => g.key === groupKey);
    if (!group || !group.options) return value;

    // Handle options that are objects with label/value pairs
    if (group.options.length > 0 && typeof group.options[0] === 'object') {
      const option = (group.options as { label: string; value: string | number }[]).find(
        o => String(o.value) === String(value)
      );
      return option?.label || value;
    }
    // Handle options that are plain strings
    else if (group.options.length > 0 && typeof group.options[0] === 'string') {
      return (group.options as string[]).includes(value) ? value : value;
    }

    return value;
  };
  const getOption = (groupKey: string) => {
    const group = filterGroups.find(g => g.key === groupKey);
    return group?.label || capitalize(cleanLabel(groupKey));
  };
  const visibleGroups = [
    ...filterGroups.filter((group) => group.dataType === "date"),
    ...filterGroups.filter((group) => group.dataType !== "date"),
  ].filter((group) =>
    group.label.toLowerCase().includes(filterSearch) ||
    group.key.toLowerCase().includes(filterSearch)
  );
  const hasFilters = Object.values(filters as Record<string, unknown>).some((raw) => {
    const values: any = raw as any;
    if (Array.isArray(values)) return values.length > 0;
    if (typeof values === 'string') return (values as string).trim() !== '';
    if (typeof values === 'boolean') return true; // treat boolean as applied
    return values !== undefined && values !== null;
  });
  const activeFilterKeys = Object.entries(filters as Record<string, unknown>).filter(([_, raw]) => {
    const values: any = raw as any;
    if (Array.isArray(values)) return values.length > 0;
    if (typeof values === 'string') return (values as string).trim() !== '';
    if (typeof values === 'boolean') return true;
    return values !== undefined && values !== null;
  });
  const visibleChips = activeFilterKeys.slice(0, 5);
  const hiddenCount = activeFilterKeys.length - visibleChips.length;
  useEffect(() => {
    if (appliedFiltersRef.current) {
      setFiltersContainerHeight?.(appliedFiltersRef.current.offsetHeight);
    }
  }, [hasFilters, visibleChips, hiddenCount, filters]);
  return isFiltersOpen ? (
    <ModalOverlay>
      <ModalContent theme={theme} ref={modalRef} style={{ overflow: 'visible', }}>
        <FormTitle title={"Apply Filters"} />
        <FormContainer theme={theme} style={{ maxWidth: 'auto' }}>
          <FilterContainer className='d-flex flex-row' >
            {/* {(isFiltersOpen) && ( */}
            <FilterPanel theme={theme} className='col-6 px-0' style={{ borderRight: `1px solid ${theme.CorporateTheme.colors.border}` }}>
              <div className='d-flex flex-row justify-content-between align-items-center mb-3 px-3'>
                <StyledText
                  fontSize={'16px'}
                  fontWeight={600}
                  color={theme.CreateFormTheme.colors.primary}
                >
                  Available Filters
                </StyledText>
              </div>
              <div className='px-3'>
                <SearchBar
                  placeholder="Search filters here"
                  style={{ width: '100%' }}
                  className='mb-3'
                  onSearchChange={(value: string) => setFilterSearch(value.toLowerCase())}
                  searchTerm={filterSearch}
                />
              </div>
              <FilterHeader>
                <SimpleBar autoHide={true} style={{ height: '65vh' }}>
                  {visibleGroups.length === 0 ?
                    <NoData
                      title='No Filters Found'
                    />  :
                    <div className="d-flex flex-column gap-3 px-3">
                      {[
                        ...visibleGroups.filter((group) => group.dataType === "date"),
                        ...visibleGroups.filter((group) => group.dataType !== "date"),
                      ].map((group) => {
                        if (group.dataType === "date") {
                          return (
                            <div key={group.key}>
                              <DateRangeFilter
                                key={group.key}
                                onDateChange={(dates) => handleDateRangeChange(group.key, dates)}
                                startDate={filters[group.key]?.[0] ? new Date(filters[group.key][0]) : null}
                                endDate={filters[group.key]?.[1] ? new Date(filters[group.key][1]) : null}
                                label={group.label}
                                routeKey={routeKey}
                                position="right"
                              />
                            </div>
                          );
                        }

                        if (group.dataType === "single-date") {
                          return (
                            <div key={group.key}>
                              <CustomInput
                                type="date"
                               rightIcon={<CalendarIcon color={theme.CorporateTheme.colors.textSecondary} />}
                                placeholder={group.label}
                                value={
                                  filters[group.key] && typeof filters[group.key] === "string"
                                    ? filters[group.key] as unknown as string
                                    : ""
                                }
                                onChange={(value: string) => handleSingleDateChange(group.key, value ? new Date(value) : null)}
                              />
                            </div>
                          );
                        }

                        return (
                          <FilterGroup
                            key={group.key}
                            ref={(el) => {
                              dropdownRefs.current[group.key] = el;
                            }}
                          >
                            <FieldLabel theme={theme}>{group.label}</FieldLabel>

                            <DropdownTrigger
                              theme={theme}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleFilterToggle(group.key);
                              }}
                            >
                              Select {group.label}
                              <ChevronDownIcon color={theme.CorporateTheme.colors.textSecondary} />
                            </DropdownTrigger>

                            {activeDropdown === group.key && (
                              <DropdownMenu theme={theme}>
                                {(group.dataType === "multi-select" || group.dataType === "select") && (
                                  <SearchBarContainer>
                                    <SearchIcon />
                                    <SearchInput
                                      type="text"
                                      placeholder="Search here..."
                                      onChange={(e) => handleSearchChange(group.key)(e.target.value)}
                                    />
                                  </SearchBarContainer>
                                )}

                                {/* 👇 Inner scrollable list */}
                                <SimpleBar autoHide={true} style={{ maxHeight: "200px" }}>
                                  {(() => {
                                    if (group.dataType === "boolean") {
                                      return ["Yes", "No"].map((value) => (
                                        <CheckboxOption key={value} theme={theme}>
                                          <CheckboxInput
                                            type="radio"
                                            name={group.key}
                                            checked={filters[group.key]?.includes(value)}
                                            onChange={() =>
                                              onFilterChange({
                                                ...filters,
                                                [group.key]: value,
                                              })
                                            }
                                          />
                                          {value}
                                        </CheckboxOption>
                                      ));
                                    }

                                    if (group.dataType === "number") {
                                      // const [min, max] = filters[group.key] ?? ["", ""];
                                      // const handleMinMaxChange = (index: 0 | 1, val: string) => {
                                      //   const updated = [...(filters[group.key] ?? ["", ""])];
                                      //   updated[index] = val;
                                      //   onFilterChange({
                                      //     ...filters,
                                      //     [group.key]: updated,
                                      //   });
                                      // };

                                      const localValues = numberRangeValues[group.key] || { min: "", max: "" };

                                      const handleMinMaxChange = (field: "min" | "max", value: string) => {
                                        const updatedValues = {
                                          ...localValues,
                                          [field]: value,
                                        };

                                        // Update local UI state immediately
                                        setNumberRangeValues(prev => ({
                                          ...prev,
                                          [group.key]: updatedValues,
                                        }));

                                        const { min, max } = updatedValues;

                                        // ✅ Only trigger API when BOTH exist
                                        if (min !== "" && max !== "") {
                                          // Optional validation: ensure min <= max
                                          if (Number(min) <= Number(max)) {
                                            onFilterChange({
                                              ...filters,
                                              [group.key]: [min, max],
                                            });
                                          }
                                        }
                                      };

                                      return (
                                        <>
                                          <CheckboxOption theme={theme}>
                                            Min :
                                            <input
                                              type="number"
                                              value={localValues.min}
                                              onChange={(e) => handleMinMaxChange("min", e.target.value)}
                                            />
                                          </CheckboxOption>
                                          <CheckboxOption theme={theme}>
                                            Max :
                                            <input
                                              type="number"
                                              value={localValues.max}
                                              onChange={(e) => handleMinMaxChange("max", e.target.value)}
                                            />
                                          </CheckboxOption>
                                        </>
                                      );
                                    }

                                    // Default (multi-select, select)
                                    const rawOptions = group.options ?? [];
                                    const isStringArray = (arr: unknown[]): arr is string[] =>
                                      arr.length > 0 && typeof arr[0] === "string";

                                    const options = isStringArray(rawOptions as unknown[])
                                      ? (rawOptions as string[]).map((opt) => ({
                                        label: opt,
                                        value: opt,
                                      }))
                                      : (rawOptions as { label: string; value: string | number }[]);

                                    const filteredOptions = options.filter(
                                      (option) =>
                                        !searchTerms[group.key] ||
                                        option.label.toLowerCase().includes(searchTerms[group.key])
                                    );

                                    if(filteredOptions.length === 0) {
                                      return <StyledText className='text-center py-2' fontSize='14px' fontWeight={500} textColor={theme.CorporateTheme.colors.textSecondary}>No result found</StyledText>
                                    }

                                    if (group.dataType === "multi-select") {
                                      return filteredOptions.map((option) => (
                                        <CheckboxOption key={option.value.toString()} theme={theme}>
                                          <CheckboxInput
                                            type="checkbox"
                                            checked={
                                              filters[group.key]?.includes(option.value.toString()) || false
                                            }
                                            onChange={handleFilterChange(
                                              group.key,
                                              option.value.toString(),
                                              group?.dataType
                                            )}
                                          />
                                          {option.label}
                                        </CheckboxOption>
                                      ));
                                    }

                                    return filteredOptions.map((option) => (
                                      <CheckboxOption key={option.value.toString()} theme={theme}>
                                        <CheckboxInput
                                          type="radio"
                                          checked={
                                            group?.dataType === 'select'
                                              ? String(filters[group.key]) === String(option.value)
                                              : (Array.isArray(filters[group.key])
                                                ? (filters[group.key] as any[]).includes(option.value.toString())
                                                : false)
                                          }
                                          onChange={handleFilterChange(
                                            group.key,
                                            option.value.toString(),
                                            group?.dataType
                                          )}
                                        />
                                        {option.label}
                                      </CheckboxOption>
                                    ));
                                  })()}
                                </SimpleBar>
                              </DropdownMenu>
                            )}
                          </FilterGroup>
                        );
                      })}
                    </div>
                  }
                </SimpleBar>

              </FilterHeader>
            </FilterPanel>
            <FilterPanel style={{ background: theme.CorporateTheme.colors.filterBG }} theme={theme} className='col-6 px-0'>
              <div className='d-flex flex-row justify-content-between align-items-center mb-3 px-3'>
                <StyledText
                  fontSize={'16px'}
                  fontWeight={600}
                  color={theme.CreateFormTheme.colors.primary}
                >
                  Applied Filters
                </StyledText>

                {(Object.keys(filters).some(key => !!filters[key] && (!Array.isArray(filters[key]) || filters[key].length > 0)
                )) && (
                    <ResetButton
                      theme={theme}
                      onClick={() => onFilterChange({})}
                    >
                      <ResetIcon color={theme.CorporateTheme.colors.textSecondary} />
                    </ResetButton>
                  )}
              </div>

              <SimpleBar autoHide={true} className='px-3' style={{ height: '70vh' }}>
                {Object.entries(filters).length === 0 ||
                  Object.values(filters).every(
                    values => values == null || (Array.isArray(values) && values.length === 0) || (typeof values === 'string' && (values as string).trim() === '')
                  ) ? (
                  <FilterChip theme={theme}>No filters are applied yet</FilterChip>
                ) : (
                  Object.entries(filters).map(([groupKey, values]) => {
                    if (values == null || (Array.isArray(values) && values.length === 0) || (typeof values === 'string' && (values as string).trim() === '')) return null;

                    const group = filterGroups.find((g) => g.key === groupKey);
                    const isDate = group?.dataType === "date" || group?.dataType === "single-date" || group?.dataType === "date-range";

                    const labelValues = Array.isArray(values)
                        ? values.map((value) => {
                          const v: any = value as any;
                          if (isDate && v) {
                            return safeFormatDate(String(v));
                          }
                          return getOptionLabel(groupKey, String(v));
                        }).join(' - ')
                        : (() => {
                          const v: any = values as any;
                          if (isDate && v) {
                            return safeFormatDate(String(v));
                          }
                          return getOptionLabel(groupKey, String(v));
                        })();

                    return (
                      <FilterChip key={groupKey} theme={theme} className='justify-content-between'>
                        <GroupOption theme={theme}>
                          {getOption(groupKey)} : &nbsp;
                          <GroupOptionLabel theme={theme}>{labelValues}</GroupOptionLabel>
                        </GroupOption>
                        <RemoveButton
                          theme={theme}
                          onClick={() => {
                            const { [groupKey]: _, ...restFilters } = filters;
                            onFilterChange(restFilters);
                          }}
                        >
                          <CloseIcon color={theme.CorporateTheme.colors.textSecondary} />
                        </RemoveButton>
                      </FilterChip>
                    );
                  })
                )}
              </SimpleBar>
            </FilterPanel>
          </FilterContainer >
        </FormContainer>
      </ModalContent>
    </ModalOverlay>
  ) : hasFilters ? (
    <AppliedFilters theme={theme} ref={appliedFiltersRef}>
      {visibleChips.map(([groupKey, values]) => {
        const group = filterGroups.find((g) => g.key === groupKey);
        const isDate =
          group?.dataType === "date" ||
          group?.dataType === "single-date" ||
          group?.dataType === "date-range";

        const labelValues = Array.isArray(values)
          ? values
              .map((value) => {
                const v: any = value as any;
                if (isDate && v) {
                  return safeFormatDate(String(v));
                }
                return getOptionLabel(groupKey, String(v));
              })
              .join(" - ")
          : (() => {
              const v: any = values as any;
              if (isDate && v) {
                return safeFormatDate(String(v));
              }
              return getOptionLabel(groupKey, String(v));
            })();

        return (
          <FilterChip
            key={groupKey}
            theme={theme}
            style={{ background: theme.colors.contentBackground }}
          >
            <GroupOption theme={theme}>{getOption(groupKey)}:</GroupOption>
            <GroupOptionLabel theme={theme}>{labelValues}</GroupOptionLabel>
            <RemoveButton
              theme={theme}
              onClick={() => {
                const { [groupKey]: _, ...restFilters } = filters;
                onFilterChange(restFilters);
              }}
            >
              <CloseIcon color={theme.CorporateTheme.colors.textSecondary} />
            </RemoveButton>
          </FilterChip>
        );
      })}

      {hiddenCount > 0 && (
        <FilterChip
          theme={theme}
          style={{
            background: theme.colors.contentBackground,
            color: theme.colors.contentText,
            fontWeight: 600,
          }}
          onClick={() => { setShowFilters(true) }}
        >
          +{hiddenCount}
        </FilterChip>
      )}

      {(Object.keys(filters).some((key) => !!filters[key] && (!Array.isArray(filters[key]) || filters[key].length > 0)
      )) && (
          <FilterChip
            theme={theme}
            onClick={() => {
              onFilterChange({});
            }}
            style={{ background: theme.colors.contentBackground }}
          >
            Reset
          </FilterChip>
        )}
    </AppliedFilters>

  ) : null;
};

export default Filters;


const SearchBarContainer = styled.div`
  height: 38px;
  background-color: #F5F5F5;
  border: 1px solid #EBEAEB;
  border-radius: 5px;
  display: flex;
  align-items: center;
  padding: 0 8px;
  margin-bottom: ${CorporateTheme.spacing.sm};
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  background-color: transparent;
  font-size: 14px;
  font-weight: 500;
  font-family: 'Red Hat Display';
  color: #767476;
  margin-left: 10px;
`;